import { useMemo } from "react";
import { NOTE_BRACKET_WIDTH_PX } from "@/lib/handwritten-callout-placement";
import { agentInkStroke } from "@/lib/sketch-stroke/agent-ink-stroke";
import { BRACKET_NIB, INK_ALPHA } from "@/lib/sketch-stroke/ink-style";
import { sketchStrokePaths } from "@/lib/sketch-stroke/sketch-mark";
import {
  noteBracketPoints,
  type NoteBracketSide,
} from "@/lib/sketch-stroke/sketch-shapes";

/** Room around the bracket for its nib and wobble. */
const BRACKET_PAD_PX = 4;

/** The pen-drawn bracket of a handwritten note, revealed by `progress`.
 * `left`/`top` are relative to the note's anchor. */
export function HandwrittenNoteBracket({
  side,
  height,
  left,
  top,
  progress,
  ink,
  seed,
  testId,
}: {
  side: NoteBracketSide;
  height: number;
  left: number;
  top: number;
  progress: number;
  ink: string;
  seed: string;
  testId: string;
}) {
  const points = useMemo(
    () => agentInkStroke(noteBracketPoints(height, side), `${seed}:bracket`),
    [height, seed, side],
  );
  const d = useMemo(
    () => sketchStrokePaths([{ points, nib: BRACKET_NIB }], progress).join(" "),
    [points, progress],
  );
  const width = NOTE_BRACKET_WIDTH_PX + BRACKET_PAD_PX * 2;
  const boxHeight = height + BRACKET_PAD_PX * 2;
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute overflow-visible"
      style={{
        left: left - BRACKET_PAD_PX,
        top: top - BRACKET_PAD_PX,
        width,
        height: boxHeight,
      }}
      viewBox={`${-BRACKET_PAD_PX} ${-BRACKET_PAD_PX} ${width} ${boxHeight}`}
      data-testid={`${testId}-callout-bracket`}
    >
      <path d={d} fill={ink} fillOpacity={INK_ALPHA} />
    </svg>
  );
}
